/**
 * Client OTP — Phase 2 pages
 * - OtpTemplates      : /otp/templates
 * - OtpDocs           : /otp/docs
 * - Enhanced re-exports imported by App.js for existing routes are handled in OtpPages.jsx.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  FileText, RefreshCcw, Plus, Trash2, Star, Copy, BookOpen,
  Terminal, Zap, Code as CodeIcon, CheckCircle2, XCircle, Clock,
} from "lucide-react";

// ─────────────── Authentication Templates page ───────────────
export function OtpTemplates() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", language: "en_US",
    code_expiration_minutes: 5, add_security_recommendation: true,
  });

  const load = async (doSync = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/otp/templates", { params: { sync: doSync } });
      setRows(data.templates || []);
      setActive(data.active_template || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load templates");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(false); }, []);

  const create = async () => {
    if (!form.name || form.name.length < 2) return toast.error("Template name is required");
    try {
      const { data } = await api.post("/otp/templates", form);
      toast.success(`Template submitted to Meta · status: ${data.status}`);
      setOpen(false);
      await load(false);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Create failed");
    }
  };

  const select = async (t) => {
    try {
      await api.post("/otp/templates/select", { template_id: t.id });
      toast.success(`Active template set to "${t.name}"`);
      await load(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This removes it from Meta too.`)) return;
    try {
      await api.delete(`/otp/templates/${t.id}`);
      toast.success("Deleted"); await load(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const statusPill = (s) => {
    s = (s || "").toUpperCase();
    const map = {
      APPROVED: { cls: "border-emerald-300 text-emerald-700 dark:text-emerald-400", icon: CheckCircle2 },
      PENDING:  { cls: "border-amber-300 text-amber-700 dark:text-amber-400", icon: Clock },
      REJECTED: { cls: "border-red-300 text-red-700 dark:text-red-400", icon: XCircle },
    };
    const { cls, icon: Icon } = map[s] || { cls: "border-zinc-300 text-zinc-500", icon: Clock };
    return <Badge variant="outline" className={`rounded-sm text-[10px] gap-1 ${cls}`}><Icon className="h-3 w-3" /> {s || "—"}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="otp-templates-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">OTP / API</div>
          <h1 className="text-3xl font-black tracking-tighter">Authentication Templates</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Create and sync AUTHENTICATION templates with Meta. Only APPROVED templates can be used for OTP delivery.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm gap-2" size="sm" onClick={() => load(true)} data-testid="sync-templates-btn">
            <RefreshCcw className="h-3.5 w-3.5" /> Sync from Meta
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm gap-2" size="sm" data-testid="new-template-btn"><Plus className="h-3.5 w-3.5" /> New template</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>Create Authentication Template</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name <span className="text-[10px] text-muted-foreground">(lowercase, underscores)</span></Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="my_app_otp_v1" className="rounded-sm font-mono" data-testid="template-name-input" />
                </div>
                <div><Label>Language</Label>
                  <select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}
                    className="w-full h-9 px-2 rounded-sm border border-border bg-background text-sm" data-testid="template-lang-select">
                    <option value="en_US">English (en_US)</option>
                    <option value="en">English (en)</option>
                    <option value="en_GB">English (en_GB)</option>
                    <option value="hi">Hindi (hi)</option>
                    <option value="es">Spanish (es)</option>
                    <option value="fr">French (fr)</option>
                    <option value="pt_BR">Portuguese (BR)</option>
                  </select>
                </div>
                <div><Label>Code expiration (minutes)</Label>
                  <Input type="number" min={1} max={90} value={form.code_expiration_minutes}
                    onChange={e => setForm({ ...form, code_expiration_minutes: Number(e.target.value) })}
                    className="rounded-sm" data-testid="template-expiry-input" />
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={form.add_security_recommendation}
                    onChange={e => setForm({ ...form, add_security_recommendation: e.target.checked })}
                    data-testid="template-security-toggle" />
                  Add security recommendation footer ("For your security, do not share this code…")
                </label>
                <div className="text-[10px] text-muted-foreground bg-muted/40 p-2 rounded-sm">
                  This creates a Meta AUTHENTICATION template with COPY_CODE button. Meta typically approves in a few minutes to a few hours.
                </div>
              </div>
              <DialogFooter>
                <Button className="rounded-sm" onClick={create} data-testid="template-create-submit">Submit to Meta</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {active && (
        <Card className="rounded-sm shadow-none border-emerald-300 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-900/10">
          <CardContent className="p-3 flex items-center gap-2 text-xs">
            <Star className="h-4 w-4 text-emerald-700 fill-emerald-500" />
            <span><b>Active template:</b> {active.name} · {active.language}</span>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm shadow-none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40"><tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2">Name</th><th className="px-3 py-2">Language</th>
              <th className="px-3 py-2">Status</th><th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Updated</th><th className="px-3 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.id} className="border-t border-border" data-testid={`template-row-${t.name}`}>
                  <td className="px-3 py-2 font-mono text-xs">
                    {t.name}
                    {t.is_default_otp && <Star className="h-3 w-3 inline-block ml-1 text-emerald-600 fill-emerald-500" />}
                  </td>
                  <td className="px-3 py-2 text-xs">{t.language}</td>
                  <td className="px-3 py-2">{statusPill(t.status)}</td>
                  <td className="px-3 py-2 text-xs">{t.category}</td>
                  <td className="px-3 py-2 text-[10px] text-muted-foreground">{t.updated_at ? new Date(t.updated_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-2 text-right space-x-1">
                    {(t.status || "").toUpperCase() === "APPROVED" && !t.is_default_otp && (
                      <Button size="sm" variant="outline" className="rounded-sm gap-1"
                        onClick={() => select(t)} data-testid={`select-template-${t.name}`}>
                        <Star className="h-3 w-3" /> Set active
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="rounded-sm text-red-700"
                      onClick={() => del(t)} data-testid={`delete-template-${t.name}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted-foreground py-10">{loading ? "Loading…" : "No templates yet. Click 'Sync from Meta' or create a new one."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─────────────── API Docs page ───────────────
const DOC_EXAMPLES = {
  curl: `# Send OTP
curl -X POST "$BASE_URL/api/v1/otp/send" \\
  -H "Authorization: Bearer $API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: order-42" \\
  -d '{"to": "+919876543210", "purpose": "login", "reference_id": "user_123"}'

# Verify OTP
curl -X POST "$BASE_URL/api/v1/otp/verify" \\
  -H "Authorization: Bearer $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"request_id": "otp_xxx", "to": "+919876543210", "otp": "123456"}'

# Status
curl -X GET "$BASE_URL/api/v1/otp/status/otp_xxx" \\
  -H "Authorization: Bearer $API_KEY"

# Resend
curl -X POST "$BASE_URL/api/v1/otp/resend" \\
  -H "Authorization: Bearer $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"request_id": "otp_xxx"}'`,

  node: `// npm install axios
const axios = require("axios");
const BASE = process.env.TEZ_BASE_URL;
const KEY  = process.env.TEZ_API_KEY;

async function sendOtp(phone, ref) {
  const { data } = await axios.post(
    \`\${BASE}/api/v1/otp/send\`,
    { to: phone, purpose: "login", reference_id: ref },
    { headers: { Authorization: \`Bearer \${KEY}\`, "Idempotency-Key": ref } }
  );
  return data; // { request_id, mode, ... }
}

async function verifyOtp(requestId, phone, code) {
  const { data } = await axios.post(
    \`\${BASE}/api/v1/otp/verify\`,
    { request_id: requestId, to: phone, otp: code },
    { headers: { Authorization: \`Bearer \${KEY}\` } }
  );
  return data; // { success: true, verified: true }
}`,

  python: `# pip install requests
import os, requests

BASE = os.environ["TEZ_BASE_URL"]
KEY  = os.environ["TEZ_API_KEY"]

def send_otp(phone: str, ref: str) -> dict:
    r = requests.post(
        f"{BASE}/api/v1/otp/send",
        headers={"Authorization": f"Bearer {KEY}", "Idempotency-Key": ref},
        json={"to": phone, "purpose": "login", "reference_id": ref},
        timeout=15,
    )
    r.raise_for_status()
    return r.json()

def verify_otp(request_id: str, phone: str, otp: str) -> dict:
    r = requests.post(
        f"{BASE}/api/v1/otp/verify",
        headers={"Authorization": f"Bearer {KEY}"},
        json={"request_id": request_id, "to": phone, "otp": otp},
        timeout=15,
    )
    r.raise_for_status()
    return r.json()`,

  php: `<?php
$BASE = getenv('TEZ_BASE_URL');
$KEY  = getenv('TEZ_API_KEY');

function tez_post($url, $body, $key) {
    $ch = curl_init($url);
    curl_setopt_array($ch, [
        CURLOPT_POST => true,
        CURLOPT_HTTPHEADER => [
            "Authorization: Bearer $key",
            "Content-Type: application/json",
        ],
        CURLOPT_POSTFIELDS => json_encode($body),
        CURLOPT_RETURNTRANSFER => true,
    ]);
    $resp = curl_exec($ch);
    curl_close($ch);
    return json_decode($resp, true);
}

// Send
$r = tez_post("$BASE/api/v1/otp/send",
    ["to" => "+919876543210", "purpose" => "login", "reference_id" => "user_1"],
    $KEY);
echo $r["request_id"];`,

  laravel: `// composer require guzzlehttp/guzzle
use GuzzleHttp\\Client;

class OtpService {
    protected $client;
    public function __construct() {
        $this->client = new Client([
            'base_uri' => env('TEZ_BASE_URL'),
            'headers'  => ['Authorization' => 'Bearer ' . env('TEZ_API_KEY')],
            'timeout'  => 15,
        ]);
    }

    public function send(string $phone, string $ref): array {
        $r = $this->client->post('/api/v1/otp/send', [
            'headers' => ['Idempotency-Key' => $ref],
            'json'    => ['to' => $phone, 'purpose' => 'login', 'reference_id' => $ref],
        ]);
        return json_decode((string) $r->getBody(), true);
    }

    public function verify(string $requestId, string $phone, string $otp): array {
        $r = $this->client->post('/api/v1/otp/verify', [
            'json' => ['request_id' => $requestId, 'to' => $phone, 'otp' => $otp],
        ]);
        return json_decode((string) $r->getBody(), true);
    }
}`,

  java: `// Java 11+ HttpClient
import java.net.URI;
import java.net.http.*;
import java.time.Duration;

public class TezOtp {
    private static final String BASE = System.getenv("TEZ_BASE_URL");
    private static final String KEY  = System.getenv("TEZ_API_KEY");
    private static final HttpClient CLIENT = HttpClient.newBuilder()
        .connectTimeout(Duration.ofSeconds(10)).build();

    public static String send(String phone, String ref) throws Exception {
        String body = String.format(
            "{\\"to\\":\\"%s\\",\\"purpose\\":\\"login\\",\\"reference_id\\":\\"%s\\"}",
            phone, ref);
        HttpRequest req = HttpRequest.newBuilder()
            .uri(URI.create(BASE + "/api/v1/otp/send"))
            .header("Authorization", "Bearer " + KEY)
            .header("Content-Type", "application/json")
            .header("Idempotency-Key", ref)
            .POST(HttpRequest.BodyPublishers.ofString(body))
            .build();
        return CLIENT.send(req, HttpResponse.BodyHandlers.ofString()).body();
    }
}`,
};

export function OtpDocs() {
  const [tab, setTab] = useState("curl");
  const [baseUrl, setBaseUrl] = useState(process.env.REACT_APP_BACKEND_URL || "https://your-app.example");

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard"); };
  const example = DOC_EXAMPLES[tab] || "";

  return (
    <div className="space-y-4" data-testid="otp-docs-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">OTP / API</div>
        <h1 className="text-3xl font-black tracking-tighter">API Documentation</h1>
        <p className="text-xs text-muted-foreground mt-1">Copy-paste snippets to integrate the OTP API into any stack.</p>
      </div>

      <Card className="rounded-sm shadow-none">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold"><BookOpen className="h-4 w-4" /> Base URL</div>
          <code className="block text-xs font-mono bg-muted/60 p-2 rounded-sm break-all" data-testid="doc-base-url">{baseUrl}</code>
          <div className="text-[11px] text-muted-foreground">Authenticate every request with <code>Authorization: Bearer &lt;ts_test_… or ts_live_…&gt;</code>. Idempotency-Key is optional but strongly recommended.</div>
        </CardContent>
      </Card>

      <Card className="rounded-sm shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><Terminal className="h-4 w-4" /> Code samples</div>
            <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => copy(example)} data-testid="doc-copy-btn">
              <Copy className="h-3 w-3" /> Copy
            </Button>
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-sm">
              <TabsTrigger value="curl" data-testid="tab-curl">cURL</TabsTrigger>
              <TabsTrigger value="node" data-testid="tab-node">Node.js</TabsTrigger>
              <TabsTrigger value="python" data-testid="tab-python">Python</TabsTrigger>
              <TabsTrigger value="php" data-testid="tab-php">PHP</TabsTrigger>
              <TabsTrigger value="laravel" data-testid="tab-laravel">Laravel</TabsTrigger>
              <TabsTrigger value="java" data-testid="tab-java">Java</TabsTrigger>
            </TabsList>
            {Object.keys(DOC_EXAMPLES).map(k => (
              <TabsContent key={k} value={k}>
                <pre className="text-[11px] font-mono bg-zinc-950 text-zinc-100 p-3 rounded-sm overflow-x-auto whitespace-pre" data-testid={`doc-${k}`}>{DOC_EXAMPLES[k]}</pre>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <Card className="rounded-sm shadow-none">
        <CardContent className="p-4 space-y-3 text-sm">
          <div className="flex items-center gap-2 font-semibold"><Zap className="h-4 w-4" /> Endpoints</div>
          <div className="grid grid-cols-1 gap-2 text-xs font-mono">
            <div><Badge className="rounded-sm">POST</Badge> <code>/api/v1/otp/send</code> — send a new OTP</div>
            <div><Badge className="rounded-sm">POST</Badge> <code>/api/v1/otp/verify</code> — verify submitted code</div>
            <div><Badge className="rounded-sm">POST</Badge> <code>/api/v1/otp/resend</code> — resend fresh code (limits: 3/req, 1/min)</div>
            <div><Badge className="rounded-sm">GET </Badge> <code>/api/v1/otp/status/&#123;request_id&#125;</code> — delivery + verification status</div>
          </div>
          <div className="border-t pt-3">
            <div className="font-semibold mb-1">Callback (webhook) signature</div>
            <div className="text-xs text-muted-foreground">If you set <code>callback_url</code> when sending, we POST every status change to that URL. Verify the <code>X-TezSandesh-Otp-Signature: t=&lt;ts&gt;, v1=&lt;hex&gt;</code> header using HMAC-SHA256 of the raw body with your derived secret.</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────── Enhanced Overview (setup checklist + KPIs) ───────────────
export function OtpOverviewV2() {
  const [setup, setSetup] = useState(null);
  const [data, setData] = useState(null);
  const load = async () => {
    try {
      const [s, d] = await Promise.all([api.get("/otp/setup"), api.get("/otp/overview")]);
      setSetup(s.data); setData(d.data);
    } catch { }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const kpi = data?.kpis || {};
  const sub = data?.subscription;

  return (
    <div className="space-y-4" data-testid="otp-overview">
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">OTP / API</div>
        <h1 className="text-3xl font-black tracking-tighter">Overview</h1>
        <p className="text-xs text-muted-foreground mt-1">Send WhatsApp OTPs from your own connected number, billed against your subscription.</p>
      </div>

      {/* Setup checklist */}
      {setup && !setup.ready && (
        <Card className="rounded-sm shadow-none border-amber-300 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-900/10">
          <CardContent className="p-4 space-y-2">
            <div className="text-sm font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-amber-700" /> Complete your OTP setup</div>
            <div className="space-y-1">
              {(setup.steps || []).map(s => (
                <a key={s.key} href={s.action_path} className="flex items-center justify-between text-xs border border-border rounded-sm p-2 hover:bg-muted/50" data-testid={`setup-step-${s.key}`}>
                  <span className="flex items-center gap-2">
                    {s.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600 fill-emerald-50" /> : <XCircle className="h-4 w-4 text-zinc-400" />}
                    <span><b>{s.label}</b>{s.value && <span className="text-muted-foreground font-mono"> · {s.value}</span>}<div className="text-[10px] text-muted-foreground">{s.hint}</div></span>
                  </span>
                  <Badge variant="outline" className={`rounded-sm text-[10px] ${s.done ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}`}>{s.done ? "Done" : "Todo"}</Badge>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { k: "today", label: "OTPs today" },
          { k: "month", label: "This month" },
          { k: "verified", label: "Verified" },
          { k: "verify_rate", label: "Verify rate", suffix: "%" },
          { k: "active_keys", label: "Active keys" },
        ].map(({ k, label, suffix }) => (
          <Card key={k} className="rounded-sm shadow-none">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
              <div className="text-3xl font-black tracking-tight" data-testid={`kpi-${k}`}>{kpi[k] ?? 0}{suffix || ""}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Plan + wallet */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="rounded-sm shadow-none">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><FileText className="h-4 w-4" /> Current subscription</div>
            {sub ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{data.plan?.name || "Custom plan"}</span>
                  <Badge variant="outline" className={`rounded-sm text-[10px] ${sub.status === "active" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}`}>{(sub.status || "").toUpperCase()}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">Included: <b>{sub.included_quota_remaining}</b> / {sub.included_quota_total}</div>
                <div className="w-full h-2 bg-muted rounded-sm overflow-hidden mt-1">
                  <div className="h-full bg-emerald-500" style={{ width: `${sub.included_quota_total ? (sub.included_quota_remaining/sub.included_quota_total)*100 : 0}%` }} />
                </div>
                <div className="text-xs text-muted-foreground">Renews: {sub.renewal_at ? new Date(sub.renewal_at).toLocaleDateString() : "—"}</div>
              </>
            ) : <div className="text-xs">No plan yet. <a className="text-orange-600 underline" href="/otp/plans">Choose a plan</a></div>}
          </CardContent>
        </Card>
        <Card className="rounded-sm shadow-none">
          <CardContent className="p-4 space-y-1 text-sm">
            <div className="flex items-center gap-2 font-semibold"><CodeIcon className="h-4 w-4" /> Wallet balance (overage)</div>
            <div className="text-3xl font-black">₹{((setup?.wallet_balance_paise ?? 0)/100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
            <a href="/wallet"><Button size="sm" variant="outline" className="rounded-sm">Manage wallet</Button></a>
          </CardContent>
        </Card>
      </div>

      {/* Volume chart */}
      <Card className="rounded-sm shadow-none">
        <CardContent className="p-4">
          <div className="text-sm font-semibold mb-2">OTP volume — last 14 days</div>
          <div className="flex items-end gap-1 h-32">
            {(data?.volume_14d || []).map(v => {
              const max = Math.max(1, ...(data?.volume_14d || []).map(x => x.count || 0));
              const h = (v.count / max) * 100;
              return (
                <div key={v.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full bg-emerald-200 dark:bg-emerald-900/40 rounded-t-sm" style={{ height: `${h}%`, minHeight: v.count ? "4px" : "1px" }} title={`${v.date}: ${v.count}`} />
                  <div className="text-[9px] text-muted-foreground">{v.date.slice(-2)}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
